import React from 'react';
import { Question, MultipleChoiceQuestion, TrueFalseQuestion, ShortAnswerQuestion } from '../types';

interface QuestionDisplayProps {
  question: Question;
  index: number;
  isDragging: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  index,
  isDragging,
  onEdit,
  onDuplicate,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
}) => {
  return (
    <div 
      className={`border border-gray-200 rounded-lg mb-4 bg-white shadow-sm transition-all ${isDragging ? 'cursor-move' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="p-4">
        <div>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">{index + 1}.</span>
                <h4 className="text-lg font-medium">{question.text || 'Untitled Question'}</h4>
              </div>
              <div className="mt-2 pl-6">
                {question.type === 'multiple_choice' && (
                  <div className="space-y-1">
                    {(question as MultipleChoiceQuestion).options.map((option, i) => (
                      <div key={option.id} className="flex items-center">
                        <span className={`mr-2 text-sm ${option.isCorrect ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                          {option.isCorrect ? '✓' : '○'}
                        </span>
                        <span className="text-gray-700">{option.text || `Option ${i + 1}`}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {question.type === 'true_false' && (
                  <div className="text-sm text-gray-700">
                    Correct answer: <span className="font-medium">{(question as TrueFalseQuestion).correctAnswer ? 'True' : 'False'}</span>
                  </div>
                )}
                
                {question.type === 'short_answer' && (
                  <div className="text-sm text-gray-700">
                    Correct answer: <span className="font-medium">{(question as ShortAnswerQuestion).correctAnswer || 'Not specified'}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-1">
              <button
                onClick={onEdit}
                className="p-1 text-gray-400 hover:text-blue-600 rounded"
                title="Edit"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
              <button
                onClick={onDuplicate}
                className="p-1 text-gray-400 hover:text-blue-600 rounded"
                title="Duplicate"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                  <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                </svg>
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-gray-400 hover:text-red-600 rounded"
                title="Delete"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500 flex justify-between">
            <div>{question.type === 'multiple_choice' ? 'Multiple choice' : question.type === 'true_false' ? 'True/False' : 'Short answer'}</div>
            <div>{question.points} {question.points === 1 ? 'point' : 'points'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDisplay; 